import React, { FC, createContext, useCallback, useState } from 'react';
import { useTheme, useMediaQuery } from '@mui/material';

import { ContextProps, Mode } from './types';


const defaultContext: ContextProps = {
  mode: window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light',
  setMode: () => null,
  isMobile: false,
  navExpanded: true,
  setNavExpanded: () => null,
  isResizing: false,
  setIsResizing: () => null,
  snackbar: {
    open: false,
    message: '',
    severity: 'info',
    autoHideDuration: 5000,
  },
  closeSnackbar: () => null,
  genericSuccess: () => null,
  genericError: () => null,
  genericInfo: () => null,
};


export const LayoutContext = createContext(defaultContext);

export const LayoutContextProvider: FC<React.PropsWithChildren> = ({ children }) => {

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [mode, setMode] = useState<Mode>('light');
  const [navExpanded, setNavExpanded] = useState(defaultContext.navExpanded);
  const [isResizing, setIsResizing] = React.useState<boolean>(false);
  const [snackbar, setSnackbar] = useState(defaultContext.snackbar);

  const closeSnackbar = useCallback(() => {
    setSnackbar({
      ...snackbar,
      open: false,
    });
  }, [snackbar]);

  const genericSuccess = useCallback((msg: string = 'Job done successfully!', hideDuration: number = 5000) => {
    setSnackbar({
      open: true,
      message: msg,
      severity: 'success',
      autoHideDuration: hideDuration,
    });
  }, []);

  const genericError = useCallback((message: string = 'Something went wrong!', hideDuration: number = 5000) => {
    setSnackbar({
      open: true,
      message,
      severity: 'error',
      autoHideDuration: hideDuration,
    });
  }, []);

  const genericInfo = useCallback((message: string = '', hideDuration: number = 5000) => {
    setSnackbar({
      open: true,
      message: message,
      severity: 'info',
      autoHideDuration: hideDuration,
    });
  }, []);

  return (
    <LayoutContext.Provider
      value={{
        mode,
        setMode,
        navExpanded,
        setNavExpanded,
        isMobile,
        isResizing,
        setIsResizing,
        snackbar,
        closeSnackbar,
        genericSuccess,
        genericError,
        genericInfo,
      }}
    >
      {children}
    </LayoutContext.Provider>
  );
};
